import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown, faCheck, faInfinity, faChartBar, faHeadset, faClock,
  faGraduationCap, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';

interface PricingPlan {
  period_days: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
}

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/subscriptions/pricing');
      const plans = (response.data.pricing || [])
        .filter((plan: PricingPlan) => plan.period_days !== 60)
        .sort((a: PricingPlan, b: PricingPlan) => {
          const order = [30, 90, 365];
          return order.indexOf(a.period_days) - order.indexOf(b.period_days);
        });
      setPricingPlans(plans);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (priceCents: number, currency: string = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(priceCents / 100);

  const calculateSavings = (plan: PricingPlan) => {
    const monthlyPlan = pricingPlans.find((p) => p.period_days === 30);
    if (!monthlyPlan || plan.period_days === 30) return null;
    const monthlyPricePerDay = monthlyPlan.price_cents / 30;
    const equivalentPrice = monthlyPricePerDay * plan.period_days;
    const savingsPercent = Math.round(((equivalentPrice - plan.price_cents) / equivalentPrice) * 100);
    return savingsPercent > 0 ? savingsPercent : null;
  };

  const getPlanLabel = (days: number) => {
    if (days === 365) return t('pricing.oneYear');
    if (days === 90) return t('pricing.threeMonths');
    if (days === 30) return t('pricing.oneMonth', '1 month');
    return `${days} ${t('pricing.days')}`;
  };

  const getPricePerMonth = (plan: PricingPlan) => {
    const monthlyPrice = (plan.price_cents / plan.period_days) * 30;
    return formatPrice(monthlyPrice, plan.currency);
  };

  const handleSelectPlan = () => {
    navigate(user ? '/certifications' : '/register', { state: { from: 'pricing' } });
  };

  const features = useMemo(
    () => [
      { icon: faInfinity, text: t('pricing.unlimitedQuestions') },
      { icon: faGraduationCap, text: t('pricing.allCertifications') },
      { icon: faChartBar, text: t('pricing.detailedAnalytics') },
      { icon: faClock, text: t('pricing.examMode') },
      { icon: faHeadset, text: t('pricing.premiumSupport') },
    ],
    [t]
  );

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div
          className="w-10 h-10 rounded-full border-2 animate-spin"
          style={{ borderTopColor: 'var(--gp-brand)', borderRightColor: 'var(--gp-brand)' }}
          role="status"
          aria-label={t('common.loading')}
        />
      </div>
    );
  }

  if (!pricingPlans.length) {
    return (
      <div className="gp-page flex items-center justify-center">
        <p className="gp-text-muted text-sm">{t('pricing.noPlans', 'Pricing plans are not available right now.')}</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t('pricing.seo.title')}
        description={t('pricing.seo.description')}
        keywords={t('pricing.seo.keywords')}
        url="/pricing"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'GlobalPreps Certification Exam Practice',
          description: t('pricing.seo.description'),
          brand: { '@type': 'Brand', name: 'GlobalPreps' },
          offers: pricingPlans.map((plan) => ({
            '@type': 'Offer',
            name: `${plan.period_days} Day Subscription`,
            price: (plan.price_cents / 100).toFixed(2),
            priceCurrency: plan.currency,
            availability: 'https://schema.org/InStock',
            url: 'https://globalpreps.com/pricing',
          })),
        }}
        breadcrumbs={[
          { name: t('nav.home'), url: '/' },
          { name: t('nav.pricing'), url: '/pricing' },
        ]}
      />

      <div className="gp-page">
        <div className="gp-container pt-6 sm:pt-8 pb-10 sm:pb-12">
          <header className="text-center mb-8 max-w-xl mx-auto">
            <span className="gp-badge mb-3 inline-flex">
              <FontAwesomeIcon icon={faCrown} aria-hidden="true" />
              {t('pricing.badge', 'Full access')}
            </span>
            <h1 className="gp-heading-xl mb-2">{t('pricing.title')}</h1>
            <p className="text-sm sm:text-base gp-text-muted">{t('pricing.subtitle')}</p>
          </header>

          <ul className="flex flex-wrap justify-center gap-2 mb-8 max-w-3xl mx-auto list-none p-0 m-0" aria-label={t('pricing.subtitle')}>
            {features.map(({ icon, text }) => (
              <li
                key={text}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md"
                style={{ background: 'var(--gp-surface)', border: '1px solid var(--gp-border)', color: 'var(--gp-ink-muted)' }}
              >
                <FontAwesomeIcon icon={icon} style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
            {pricingPlans.map((plan) => {
              const savings = calculateSavings(plan);
              const isPopular = plan.period_days === 90;
              const isBestValue = plan.period_days === 365;

              return (
                <div
                  key={plan.period_days}
                  className={`gp-pricing-card ${
                    isPopular ? 'gp-pricing-card-popular' : ''
                  } ${isBestValue ? 'gp-pricing-card-best' : ''}`}
                >
                  {isPopular && (
                    <span className="gp-pricing-badge">{t('pricing.mostPopular')}</span>
                  )}
                  {isBestValue && !isPopular && (
                    <span className="gp-pricing-badge gp-pricing-badge-accent">{t('pricing.bestValue')}</span>
                  )}
                  {savings && savings > 0 && (
                    <span
                      className="absolute -top-2 -right-2 text-[10px] font-bold w-10 h-10 rounded-full flex flex-col items-center justify-center leading-tight"
                      style={{ background: 'var(--gp-success)', color: '#fff' }}
                    >
                      <span>{t('pricing.save')}</span>
                      <span>{savings}%</span>
                    </span>
                  )}

                  <div className="text-center mb-4">
                    <h2 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                      {getPlanLabel(plan.period_days)}
                    </h2>
                    <div className="font-display text-3xl font-bold" style={{ color: 'var(--gp-ink)' }}>
                      {formatPrice(plan.price_cents, plan.currency)}
                    </div>
                    <p className="text-xs gp-text-muted mt-1">
                      {plan.period_days === 30
                        ? t('pricing.perMonth')
                        : `${getPricePerMonth(plan)} ${t('pricing.perMonth')}`}
                    </p>
                  </div>

                  <ul className="space-y-2 mb-5 flex-1 text-sm" style={{ color: 'var(--gp-ink-muted)' }}>
                    {[t('pricing.unlimitedAccess'), t('pricing.allQuestions'), t('pricing.examMode'), t('pricing.detailedAnalytics'), t('pricing.premiumSupport')].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <FontAwesomeIcon icon={faCheck} className="mt-0.5 shrink-0 text-xs" style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={handleSelectPlan}
                    className={isPopular ? 'gp-btn-primary w-full' : isBestValue ? 'gp-btn-accent w-full' : 'gp-btn-ghost w-full'}
                    style={!isPopular && !isBestValue ? { borderColor: 'var(--gp-brand)', color: 'var(--gp-brand)' } : undefined}
                  >
                    {user ? t('pricing.selectPlan') : t('pricing.getStarted')}
                  </button>
                </div>
              );
            })}
          </div>

          <div
            className="gp-cta-band text-center max-w-2xl mx-auto"
            style={{ background: 'linear-gradient(135deg, var(--gp-ink) 0%, #1a3340 55%, var(--gp-brand) 100%)', color: '#fff' }}
          >
            <h2 className="font-display text-xl font-semibold mb-2">{t('pricing.readyToStart')}</h2>
            <p className="opacity-90 mb-4 text-sm max-w-md mx-auto">{t('pricing.ctaDescription')}</p>
            <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
              {!user ? (
                <>
                  <LocalizedLink
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-md px-5 py-2 text-sm bg-white transition-colors hover:bg-white/90"
                    style={{ color: 'var(--gp-brand)' }}
                  >
                    {t('auth.signUp')}
                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </LocalizedLink>
                  <LocalizedLink
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-md px-5 py-2 text-sm border border-white/30 hover:bg-white/10 transition-colors"
                  >
                    {t('auth.login')}
                  </LocalizedLink>
                </>
              ) : (
                <LocalizedLink
                  to="/certifications"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-md px-5 py-2 text-sm bg-white"
                  style={{ color: 'var(--gp-brand)' }}
                >
                  {t('pricing.browseCertifications')}
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </LocalizedLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
